import type { User } from 'firebase/auth';
import { recordFeatureUseEvent } from '@shared/services/analyticsService';
import type { ProductLine } from '@shared/types';

/**
 * Paywall-funnel analytics events: impression → purchase_started →
 * purchase_completed / dismissed.
 *
 * Every event carries {source, product} so the coming three-product chooser
 * (spending / debt / all) lands into the same schema with zero migration —
 * product is simply null for today's single-offer modal.
 *
 * Names must match functions-enough/analytics/funnels.py (PAYWALL_FEATURE /
 * PAYWALL_ACTIONS) — the nightly rollup aggregates them into
 * rollup.paywallFunnel keyed by source AND product.
 *
 * Fired from the paywall SEAMS (SubscriptionContext showPaywall/hidePaywall
 * and the thin local PaywallModal adapter), never from the modal's layout
 * internals — the UI is due for a reshape and only signals outcomes.
 */

export type PaywallFunnelAction =
    | 'impression'
    | 'purchase_started'
    | 'purchase_completed'
    | 'dismissed';

/** Fire-and-forget paywall funnel event.
 *
 *  ANONYMOUS uids record too (changed 2026-07-19): under the paid launch the
 *  most important funnel step — the pre-login carousel paywall — happens on
 *  the visitor's anonymous uid, and dropping those events made the launch
 *  funnel internally invisible (GA4-only). Events land under the anon uid;
 *  the nightly rollup canonicalizes linked uids via common/identity.py, and
 *  event COUNTS (impressions/starts/completions) are correct regardless of
 *  whether the anon uid ever links. `anonymous: true` in meta keeps the two
 *  populations separable in analysis. */
export const recordPaywallFunnelEvent = (
    user: Pick<User, 'uid' | 'isAnonymous'> | null | undefined,
    action: PaywallFunnelAction,
    context: { source?: string; product?: ProductLine | null },
): void => {
    if (!user) return;
    recordFeatureUseEvent(user.uid, 'paywall', action, {
        oncePerSession: false,
        meta: {
            source: context.source ?? 'unknown',
            product: context.product ?? undefined,
            ...(user.isAnonymous ? { anonymous: true } : {}),
        },
    }).catch(() => {});
};
